'use client';
import { ScoredStock } from '@/lib/compute';
import { getSectorColor } from './ControlPanel';
import { compositeScore, valuationVerbal, qualityVerbal, prodQuality } from '@/lib/composite-score';

import { compositeTier as sharedTier } from '@/lib/composite-tiers';
function compositeTier(c: number | null): { label: string; color: string; bg: string; border: string } {
  const t = sharedTier(c);
  // Adapt to legacy local interface used downstream (color/bg/border).
  return { label: t.label, color: t.hex, bg: `${t.hex}20`, border: `${t.hex}40` };
}

function fmt(v: number | null, decimals = 2, suffix = '') {
  if (v === null || v === undefined) return '--';
  return v.toFixed(decimals) + suffix;
}
function fmtPct(v: number | null) { return fmt(v ? v * 100 : v, 1, '%'); }
function fmtMkt(v: number) {
  if (v >= 1e12) return `$${(v / 1e12).toFixed(1)}T`;
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  return `$${(v / 1e6).toFixed(0)}M`;
}

function ScorePill({ label, value, color }: { label: string; value: number | null; color: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[#e2e8f0] text-[10px]">{label}</span>
      <span className="text-xs font-bold tabular-nums" style={{ color, fontFamily: 'var(--font-mono)' }}>
        {fmt(value)}
      </span>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between items-baseline">
      <span className="text-[#e2e8f0] text-[10px]">{label}</span>
      <span className="text-[#e2e8f0] text-[10px] tabular-nums" style={{ fontFamily: 'var(--font-mono)' }}>
        {value}
      </span>
    </div>
  );
}

function SectionHeader({ label }: { label: string }) {
  return (
    <div className="text-[9px] font-semibold uppercase tracking-widest text-white mt-2 mb-0.5 pt-1.5 border-t border-[#1e293b]">
      {label}
    </div>
  );
}

export function StockTooltip({ stock, x, y, flipX, flipY, shortTermTechnical, longTermTechnical, combinedTechnical, mode = 'full' }: { stock: ScoredStock; x: number; y: number; flipX?: boolean; flipY?: boolean; shortTermTechnical?: number | null; longTermTechnical?: number | null; combinedTechnical?: number | null; mode?: 'full' | 'technical' }) {
  const f = stock.factors;
  const sectorColor = getSectorColor(stock.sector);

  return (
    <div
      className="absolute z-50 pointer-events-none"
      style={{
        ...(flipX ? { right: -x + 16 } : { left: x + 16 }),
        ...(flipY ? { bottom: -y + 12 } : { top: y - 12 }),
      }}
    >
      <div
        className="rounded-xl p-3.5 shadow-2xl border border-[#1e293b]"
        style={{
          background: 'rgba(13, 17, 23, 0.95)',
          backdropFilter: 'blur(12px)',
          minWidth: 240,
          maxWidth: 280,
        }}
      >
        {/* Header */}
        <div className="flex items-start gap-2 mb-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-white font-bold text-sm" style={{ fontFamily: 'var(--font-mono)' }}>
                {stock.symbol}
              </span>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                style={{ color: sectorColor, backgroundColor: `${sectorColor}15`, border: `1px solid ${sectorColor}30` }}>
                {stock.sector}
              </span>
            </div>
            <div className="text-[#e2e8f0] text-[11px] mt-0.5 truncate">{stock.name}</div>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-[#e2e8f0] text-[10px]" style={{ fontFamily: 'var(--font-mono)' }}>
                {fmtMkt(stock.marketCap)}
              </span>
              {(() => {
                const c = compositeScore(prodQuality(stock), stock.valuationScore);
                const t = compositeTier(c);
                return (
                  <span
                    className="text-[9px] px-1.5 py-0.5 rounded tabular-nums font-semibold flex items-baseline gap-1"
                    style={{ color: t.color, backgroundColor: t.bg, border: `1px solid ${t.border}`, fontFamily: 'var(--font-mono)' }}
                  >
                    <span className="opacity-70 uppercase tracking-wider">comp</span>
                    <span>{c == null ? '—' : c.toFixed(2)}</span>
                    <span className="opacity-80 uppercase tracking-wider">{t.label}</span>
                  </span>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Score pills */}
        <div className="flex gap-4 mb-1 pb-2 border-b border-[#1e293b] flex-wrap items-baseline">
          <div className="flex items-baseline gap-1.5">
            <ScorePill label="Quality" value={prodQuality(stock)} color="#22c55e" />
            <span className="text-[8px] lowercase tracking-wide" style={{ color: qualityVerbal(prodQuality(stock)).color }}>
              {qualityVerbal(prodQuality(stock)).word}
            </span>
          </div>
          {mode === 'full' && (
            <div className="flex items-baseline gap-1.5">
              <ScorePill label="Value" value={stock.valuationScore} color="#3b82f6" />
              <span className="text-[8px] lowercase tracking-wide" style={{ color: valuationVerbal(stock.valuationScore).color }}>
                {valuationVerbal(stock.valuationScore).word}
              </span>
            </div>
          )}
        </div>

        {/* Technical scores (when provided) */}
        {(shortTermTechnical !== undefined || longTermTechnical !== undefined || combinedTechnical !== undefined) && (
          <div className="flex gap-4 mb-1 pb-2 border-b border-[#1e293b] flex-wrap">
            <span className="text-[9px] font-semibold uppercase tracking-widest text-white">Technical</span>
            {shortTermTechnical !== undefined && (
              <ScorePill label="Short" value={shortTermTechnical} color="#f97316" />
            )}
            {longTermTechnical !== undefined && (
              <ScorePill label="Long" value={longTermTechnical} color="#f97316" />
            )}
            {combinedTechnical !== undefined && (
              <ScorePill label="Combined" value={combinedTechnical} color="#f59e0b" />
            )}
          </div>
        )}

        {/* Pillar scores */}
        <div className="grid grid-cols-4 gap-1 py-1.5 mb-0.5">
          {(['profitability', 'growth', 'safety', 'shareholderReturns'] as const).map(p => (
            <div key={p} className="text-center">
              <div className="text-[8px] text-white uppercase tracking-wide">{p === 'shareholderReturns' ? 'ShrR' : p.slice(0, 4)}</div>
              <div className="text-[10px] text-[#e2e8f0] font-semibold tabular-nums" style={{ fontFamily: 'var(--font-mono)' }}>
                {fmt(stock.pillarScores[p], 1)}
              </div>
            </div>
          ))}
        </div>

        {/* Factor details */}
        <div className="space-y-0">
          <SectionHeader label="Profitability" />
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
            <Row label="ROE" value={fmtPct(f.roe)} />
            <Row label="ROA" value={fmtPct(f.roa)} />
            <Row label="ROIC" value={fmtPct(f.roic)} />
            <Row label="GP/Assets" value={fmt(f.grossProfitToAssets, 2, 'x')} />
            <Row label="Gross Mgn" value={fmtPct(f.grossMargin)} />
            <Row label="Net Mgn" value={fmtPct(f.netMargin)} />
            <Row label="CFO/Assets" value={fmt(f.cfoToAssets, 2, 'x')} />
          </div>

          <SectionHeader label="Growth" />
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
            <Row label="Fwd EPS 1yr" value={fmtPct(f.fwdEpsGrowth)} />
            <Row label="Fwd Rev 1yr" value={fmtPct(f.fwdRevGrowth)} />
            <Row label="Fwd EPS 2yr" value={fmtPct(f.fwdEpsCagr2yr)} />
            <Row label="Fwd Rev 2yr" value={fmtPct(f.fwdRevCagr2yr)} />
            <Row label="EPS 3yr" value={fmtPct(f.epsCAGR3yr)} />
            <Row label="FCF 3yr" value={fmtPct(f.fcfGrowth3yr)} />
            <Row label="GM Exp 3yr" value={fmtPct(f.grossMarginExpansion3yr)} />
            <Row label="OM Exp 3yr" value={fmtPct(f.opMarginExpansion3yr)} />
          </div>

          <SectionHeader label="Safety" />
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
            <Row label="Altman Z" value={fmt(f.altmanZ, 1)} />
            <Row label="Piotroski" value={`${f.piotroski ?? '--'}/9`} />
            <Row label="D/EBITDA" value={fmt(f.debtToEbitda, 1, 'x')} />
            <Row label="Int Cov" value={fmt(f.interestCoverage, 1, 'x')} />
            <Row label="Beta" value={fmt(f.beta, 2)} />
            <Row label="Curr Ratio" value={fmt(f.currentRatio, 1, 'x')} />
            <Row label="FCF/Debt" value={fmt(f.fcfToDebt, 2, 'x')} />
          </div>

          <SectionHeader label="Shareholder Returns" />
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
            <Row label="Buyback" value={fmtPct(f.buybackYield)} />
            <Row label="Div Yield" value={fmtPct(f.dividendYield)} />
            <Row label="Debt Paydown" value={fmtPct(f.netDebtPaydownYield)} />
          </div>

          {mode === 'full' && (
            <>
              <SectionHeader label="Valuation" />
              <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                <div className="flex justify-between items-baseline">
                  <span className="text-[#e2e8f0] text-[10px]">Fwd P/E 1yr</span>
                  <span className="text-[#e2e8f0] text-[10px] tabular-nums flex items-center gap-1" style={{ fontFamily: 'var(--font-mono)' }}>
                    {fmt(f.fwdPE, 1, 'x')}
                    {stock.hasOverride && <span className="text-[8px] px-1 py-px rounded bg-[#f59e0b20] text-[#f59e0b] font-semibold">BBG</span>}
                  </span>
                </div>
                <Row label="Fwd P/E 2yr" value={fmt(f.fwdPE2yr, 1, 'x')} />
                <Row label="Fwd EV/Rev" value={fmt(f.fwdEvToRevenue, 1, 'x')} />
                <Row label="PEG" value={fmt(f.pegRatio, 1, 'x')} />
                <Row label="EV/EBITDA" value={fmt(f.evToEbitda, 1, 'x')} />
                <Row label="P/FCF" value={fmt(f.pToFcf, 1, 'x')} />
                <Row label="EV/GP" value={fmt(f.evToGrossProfit, 1, 'x')} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
